import { useContext, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { JournalType } from '@/pages/safe/safeJournalApi';

import { WriteHereContext } from './writeHere';

/**
 * 안 쓴 것을 알리는 글이 가는 자리(사용자 지시 2026-10-27 「미등록 · 미입력 글씨는 눌러서 바로 작성하는 곳으로」).
 * 일지는 그 현장의 새 일지 창(`?new=1`), 미조치는 그 현장의 시정조치. 사유 글은 서버 SafeDashboardService 의 글자다.
 * TBM 은 TBM 일지(V572 · DOC-136)로 — 대시보드는 TBM 일지 · 안전보건일지의 TBM 칸 · 포털 TBM 을 함께 센다(SafeDashboardMapper).
 */
function writePathOf(text: string): string | null {
  if (text.startsWith('순회점검')) return 'patrol?new=1';
  if (text.startsWith('합동점검')) return 'joint?new=1';
  if (text.includes('TBM')) return 'tbm?new=1';
  if (text.includes('미조치')) return 'actions';
  return null;
}

/** 새 일지로 가는 글의 일지 종류 — 그 자리에서 작성 창을 띄울 때 쓴다. 미조치는 일지가 아니라 목록이다. */
function writeTypeOf(text: string): JournalType | null {
  if (text.startsWith('순회점검')) return 'PATROL';
  if (text.startsWith('합동점검')) return 'JOINT';
  if (text.includes('TBM')) return 'TBM';
  return null;
}

/**
 * 누르면 작성 자리로 가는 글. 갈 자리가 없는 글(기온)은 그냥 글이다. 화면이 WriteHereContext 를 깔았으면 일지는
 * 그 화면 위에 작성 창을 띄운다(화면이 바뀌지 않는다).
 */
export function FixLink({ siteId, text, children }: { siteId: string; text: string; children?: ReactNode }) {
  const navigate = useNavigate();
  const writeHere = useContext(WriteHereContext);
  const path = writePathOf(text);
  const type = writeTypeOf(text);
  if (!path) return <>{children ?? text}</>;
  return (
    <button
      type="button"
      className="fix-link"
      onClick={(e) => {
        e.stopPropagation();
        if (writeHere && type) {
          writeHere(siteId, type);
          return;
        }
        navigate(`/sites/${encodeURIComponent(siteId)}/${path}`);
      }}
    >
      {children ?? text}
    </button>
  );
}

/** 사유 줄 — 「 · 」 로 잇되 하나하나 누를 수 있다. */
export function FixReasons({ siteId, reasons }: { siteId: string; reasons: string[] }) {
  return (
    <>
      {reasons.map((reason, index) => (
        <span key={reason}>
          {index > 0 && ' · '}
          <FixLink siteId={siteId} text={reason} />
        </span>
      ))}
    </>
  );
}
